import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  CONTENTFUL_PAGE_NAME,
  ELEMENT_ACTION,
  ICON_TYPES,
  NOTIFICATION_TYPES,
} from '../../../shared/constants';
import { CONTENTFUL_FORM_NAME } from '../../../shared/constants/Contentful.constants';
import { EMAIL_SUCCESS } from '../../../shared/constants/EmailService.constants';
import { LeadType } from '../../../shared/constants/Tracking.constants';
import {
  IContactForm,
  IContentfulLink,
  IFormValues,
  IParentPage,
} from '../../../types/shared';
import {
  GatsbyPageContext,
  ICard,
  ICardWrapper,
  Tag,
} from '../../../types/shared/shared.types';

import ConsentCheckbox from '../../atoms/consent-checkbox/ConsentCheckbox';
import FormMessage, { FORM_TYPES } from '../../atoms/form-message/FormMessage';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import {
  DEQ_COMPS,
  DEQ_ERROR_TYPES,
  DEQ_PAGE_TYPES,
  DEQ_TYPES,
} from '../../../utils/deq';
import {
  FORM_TRACKING_STATUS,
  trackFormStepEvent,
} from '../../../utils/deq/formTrackPageView';
import { trackInputErrors } from '../../../utils/deq/trackInputErrors';
import { copySelectorHelper } from '../../../utils/deq/trackInputErrors.helpers';
import { StyledPrimaryButton } from '../../../utils/forms/buttonStyles';
import { renderFormInput } from '../../../utils/forms/renderFormInput';
import { getInternalLink, ReactMarkdownExtended } from '../../../utils/generic';
import { renderBodyItem } from '../../../utils/generic/renderBodyItem';
import {
  LABELS,
  languageHelper,
  TITLES,
  TRACKING_LABELS,
} from '../../../utils/language';
import {
  getDescription,
  setMessageBasedOnCode,
} from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { contactFormData } from '../../../data/static-content/forms/contact-form/static-content';
import {
  createGraphMailTemplate,
  handleReCaptcha,
  IErrorResponse,
  ISuccessResponse,
} from '../../../services/re-captcha.service';
import { LocalStorage } from '../../../tokens';
import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';
import FormStatusCard from '../../molecules/form-status-card/FormStatusCard';
import StepHeader, {
  CANCEL_TYPE,
} from '../../molecules/step-header/StepHeader';
import Form from '../form/Form';
import FormResultBody from '../form-result-body/FormResultBody';

import { FormContext } from './ContactFormContext';

interface Props {
  name: string;
  title: string;
  parentPage: IParentPage;
  form: IContactForm;
  subtitle?: string;
  onClose: () => void;
  externalParentPage: IContentfulLink;
  successCards: ICard[] | ICardWrapper[];
  trackingName?: string;
  pageContext: GatsbyPageContext;
  tags: Tag[];
  formTrackingType?: string;
}

const parseDEQFormName = (subtitle?: string, trackingName?: string) => {
  const pageName = subtitle !== CONTENTFUL_PAGE_NAME.home ? ` ${subtitle}` : '';

  return `contact-form${pageName}${
    trackingName ? ` ${trackingName}` : ''
  }`.toLowerCase();
};

const ContactForm = ({
  name,
  title,
  subtitle,
  parentPage,
  form,
  onClose,
  externalParentPage,
  successCards,
  trackingName,
  pageContext,
  tags,
  formTrackingType = LeadType.LEAD_HARD,
}: Props) => {
  const [validForm, setValidForm] = useState(false);
  const [formResultData, setFormResultData] = useState({});
  const [errorFormActive, setErrorFormActive] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [toggleClick, setToggleClick] = useState(false);
  const { lang } = languageHelper();
  const { primaryLink, secondaryLink, email } = form;
  const formRef = useRef<HTMLFormElement>(null);

  const submitUrl = externalParentPage
    ? externalParentPage.externalUrl
    : `/${getInternalLink(
        (primaryLink?.linkedPage as IParentPage) || parentPage
      )}`;
  const cancelUrl = externalParentPage
    ? externalParentPage.externalUrl
    : `/${getInternalLink(
        (secondaryLink?.linkedPage as IParentPage) || parentPage
      )}`;

  const formattedDeqFormName = parseDEQFormName(subtitle, trackingName);

  const callback = (response: ISuccessResponse) => {
    if (response.message === EMAIL_SUCCESS.SEND) {
      setValidForm(true);

      let formOutcome;

      if (name === CONTENTFUL_FORM_NAME.threadstone) {
        formOutcome =
          localStorage.getItem(LocalStorage.THREADSTONE_ID) || 'no-id-found';
      }

      trackFormStepEvent(
        DEQ_PAGE_TYPES.contact,
        form.id,
        FORM_TRACKING_STATUS.endSuccess,
        formattedDeqFormName,
        formOutcome,
        formTrackingType
      );
    }
    if (response) {
      setIsLoading(false);
    }

    if (primaryLink?.externalUrl) {
      window.open(primaryLink.externalUrl, '_blank');
    }
    return response;
  };

  const errCallback = (response: IErrorResponse) => {
    const {
      statusCode,
      statusText,
      url,
      error,
      name: statusName,
      message,
    } = response;
    const isTypeError = response instanceof TypeError;

    setErrorFormActive(true);
    setIsLoading(false);
    trackFormStepEvent(
      DEQ_PAGE_TYPES.contact,
      form.id,
      FORM_TRACKING_STATUS.endFail,
      formattedDeqFormName,
      undefined,
      formTrackingType,
      {
        type: DEQ_ERROR_TYPES.technical,
        code: isTypeError ? statusName : statusCode,
        source: url,
        message: isTypeError
          ? message
          : statusText ||
            error ||
            setMessageBasedOnCode(statusCode) ||
            TRACKING_LABELS.errorMessage[lang],
      }
    );
  };

  const handleSubmit = async (formResult: IFormValues) => {
    if (typeof window === 'undefined') {
      return;
    }

    setIsLoading(true);
    setFormResultData(formResult);

    let leadID = '';

    if (name === CONTENTFUL_FORM_NAME.threadstone) {
      leadID = localStorage.getItem(LocalStorage.THREADSTONE_ID) || uuidv4();

      localStorage.setItem(LocalStorage.THREADSTONE_ID, leadID);
    }

    const emailTemplate = createGraphMailTemplate(
      { ...formResult, threadstoneLeadID: leadID },
      email.emailAddresses,
      email.title,
      email.body
    );

    handleReCaptcha(emailTemplate, callback, errCallback);
  };

  useEffect(() => {
    if (!validForm && !isLoading) {
      if (formRef) {
        const requiredCopy = copySelectorHelper(
          formRef,
          '[data-testid="notification-text"]',
          lang
        );

        trackInputErrors(DEQ_TYPES.formInput, requiredCopy);
      }
    }
  }, [toggleClick]);

  const onClickSubmitHandler = () => {
    setToggleClick(!toggleClick);
  };

  return (
    <div>
      <FormContext.Provider value={validForm ? formResultData : undefined}>
        <StepHeader
          title={validForm ? TITLES.success[lang] : title}
          label={validForm ? undefined : subtitle}
          cancelUrl={cancelUrl}
          cancelLabel={validForm ? LABELS.close[lang] : LABELS.cancel[lang]}
          cancelType={validForm ? CANCEL_TYPE.back : CANCEL_TYPE.close}
        />
        <StyledContentWrapper>
          <Inner>
            {validForm && (
              <FormResultBody
                title={
                  form.successTitle ||
                  contactFormData.userFeedback.success.title[lang]
                }
                subtitle={
                  form.successDescription &&
                  getDescription(
                    form.successDescription?.childMarkdownRemark
                      ?.rawMarkdownBody,
                    { url: primaryLink?.externalUrl || '' }
                  )
                }
                open={validForm}
              >
                {successCards?.map((item, key) =>
                  renderBodyItem(
                    item,
                    key,
                    pageContext.pageType,
                    tags,
                    pageContext.slug,
                    parentPage || externalParentPage || '/'
                  )
                )}
              </FormResultBody>
            )}
            <FormStatusCard
              title={contactFormData.userFeedback.error.title[lang]}
              statusType={NOTIFICATION_TYPES.error}
              show={errorFormActive}
              primaryButtonLabel={
                contactFormData.userFeedback.error.primaryButtonText[lang]
              }
              customPrimaryClick={() => setErrorFormActive(false)}
              secondaryButtonUrl={cancelUrl}
            >
              <ReactMarkdownExtended componentType={DEQ_COMPS.contactForm}>
                {contactFormData.userFeedback.error.description[lang]}
              </ReactMarkdownExtended>
            </FormStatusCard>
            <FormStatusCard
              statusType={NOTIFICATION_TYPES.loading}
              show={isLoading}
            >
              <ReactMarkdownExtended componentType={DEQ_COMPS.contactForm}>
                {contactFormData.userFeedback.loading.email[lang]}
              </ReactMarkdownExtended>
            </FormStatusCard>
            {!isLoading && !errorFormActive && !validForm && (
              <>
                <BodyIntro>
                  {form.rawSubtitle?.childMarkdownRemark && (
                    <ReactMarkdownExtended
                      componentType={DEQ_COMPS.contactForm}
                    >
                      {form.rawSubtitle.childMarkdownRemark.rawMarkdownBody}
                    </ReactMarkdownExtended>
                  )}
                </BodyIntro>
                {form.showPrivacyStatement && (
                  <FormMessage
                    type={FORM_TYPES.info}
                    isNotificationOpen={isNotificationOpen}
                    onNotificationClick={(event) =>
                      setIsNotificationOpen(event)
                    }
                  >
                    <ReactMarkdownExtended
                      componentType={DEQ_COMPS.contactForm}
                    >
                      {
                        contactFormData.infoMessage.privacyPolicyAndReCaptcha[
                          lang
                        ]
                      }
                    </ReactMarkdownExtended>
                  </FormMessage>
                )}
                {form.description && (
                  <BodyNormal data-testid="description">
                    <ReactMarkdownExtended
                      componentType={DEQ_COMPS.contactForm}
                    >
                      {form.description.childMarkdownRemark.rawMarkdownBody}
                    </ReactMarkdownExtended>
                  </BodyNormal>
                )}
                <Form
                  deqFormName={formattedDeqFormName}
                  stepName={`${title}`.toLowerCase()}
                  id={form.id}
                  submit={handleSubmit}
                  deqFormType={formTrackingType}
                  ref={formRef}
                >
                  {form.cards ? (
                    form.cards.map((card, key: number) => (
                      <Card key={`form-card-number-${key}`}>
                        <CardTitle>{card.title}</CardTitle>
                        {card.body && (
                          <CardBody>
                            <ReactMarkdownExtended
                              componentType={DEQ_COMPS.contactForm}
                            >
                              {card.body.childMarkdownRemark.rawMarkdownBody}
                            </ReactMarkdownExtended>
                          </CardBody>
                        )}
                        {card.formInputs &&
                          card.formInputs.map((item, index) =>
                            renderFormInput(item, index)
                          )}
                        {card.consentInputs && (
                          <ConsentWrapper>
                            {card.consentInputs.map((item, index) => {
                              return (
                                <ConsentCheckbox
                                  key={index}
                                  id={item.id}
                                  name={item.valueId}
                                  label={item.label}
                                  isRequired={item.required}
                                  helperMessage={item?.helperMessage}
                                  tooltip={item?.tooltip}
                                />
                              );
                            })}
                          </ConsentWrapper>
                        )}
                      </Card>
                    ))
                  ) : (
                    <Card>
                      {form.formInputs?.map((item, index) =>
                        renderFormInput(item, index)
                      )}
                    </Card>
                  )}
                  <CtaWrapper>
                    <StyledPrimaryButton
                      type={BUTTON_TYPES.submit}
                      data-testid="submit-button"
                      buttonAction={BUTTON_TYPES.submit}
                      label={LABELS.submit[lang]}
                      icon={ICON_TYPES.none}
                      buttonTheme={BUTTON_THEMES.highlight}
                      customOnClick={() =>
                        setTimeout(() => {
                          onClickSubmitHandler();
                        }, 0)
                      }
                      deqComponentTitle="Indienen-form"
                      deqCustomAttributes={{
                        componentType: 'contact-form',
                        elementTarget: submitUrl,
                        elementAction: BUTTON_TYPES.submit,
                      }}
                    />
                    <SecondaryButton
                      type="internal"
                      to={cancelUrl}
                      icon={ICON_TYPES.default}
                      label={LABELS.cancel[lang]}
                      deqComponentTitle="cancel-form"
                      deqCustomAttributes={{
                        componentType: 'contact-form',
                        elementTarget: cancelUrl,
                        elementAction: ELEMENT_ACTION.close,
                      }}
                    />
                  </CtaWrapper>
                </Form>
              </>
            )}
          </Inner>
        </StyledContentWrapper>
      </FormContext.Provider>
    </div>
  );
};

export default ContactForm;

const StyledContentWrapper = styled(ContentWrapper)`
  display: flex;
  justify-content: center;
  padding-bottom: ${Spacings.M};
  padding-top: ${Spacings.M};
`;

const formParagraphStyles = css`
  color: ${Colors.abnBlack};
  margin-bottom: ${Spacings.M};

  b {
    font-weight: bold;
  }

  p {
    margin: 0 0 ${Spacings.S};

    &:last-of-type {
      margin: 0;
    }
  }
`;

const BodyIntro = styled(IntroText)`
  ${formParagraphStyles}
`;

const BodyNormal = styled(Body)`
  ${formParagraphStyles}
  h3 {
    margin: 0 0 ${Spacings.XS};
  }

  h4 {
    margin-bottom: ${Spacings.XS};
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  ${mq.from.breakpoint.L`
    width: 736px;
  `}
`;

const Card = styled.div`
  background-color: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  margin-bottom: ${Spacings.XS};
  padding: ${Spacings.S};

  &:last-child {
    margin-bottom: 0;
  }

  & > * {
    margin-bottom: ${Spacings.M};
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
  `}
`;

const CardTitle = styled(H3)`
  margin-bottom: ${Spacings.XS};
  margin-top: 0;
`;

const CardBody = styled(Body)`
  color: ${Colors.abnNeutral400};
`;

const ConsentWrapper = styled.div`
  & > * {
    margin-bottom: ${Spacings.XS};
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const CtaWrapper = styled.div`
  margin: ${Spacings.M} 0px ${Spacings.XS} 0px;

  ${mq.from.breakpoint.M`
    margin: ${Spacings.XL} 0px ${Spacings.S} 0px;
  `}
`;
